import React, { useState } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import { DashboardLocationCard } from '../dashboardTypes'

const VISIBLE_SITES = 3

const Card = styled.button`
  width: 100%;
  min-height: 14rem;
  background: ${palette.white};
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.75rem;
  box-shadow: ${(props) => props.theme.dropShadows.normal};
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow: hidden;
  box-sizing: border-box;
  cursor: pointer;
  text-align: left;
  font: inherit;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${palette.grey.grey300};
  }

  &:focus-visible {
    outline: 2px solid ${palette.accent.teal500};
    outline-offset: 2px;
  }
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`

const ActiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.625rem;
  border-radius: 999px;
  background: ${palette.accent.teal100};
  color: ${palette.accent.teal700};
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex-shrink: 0;
`

const Metrics = styled.div`
  display: flex;
  gap: 2rem;
`

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`

const Sites = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`

const SiteChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  border: 1px solid ${palette.grey.grey200};
  background: ${palette.white};
  color: ${palette.text.secondary};
  font-size: 0.875rem;
  font-weight: 500;
`

const SiteCount = styled.span`
  color: ${palette.text.primary};
  font-weight: 600;
`

const MoreSites = styled.span`
  width: fit-content;
  color: ${palette.text.secondary};
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: auto;
`

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 0.375rem;
  background: ${palette.grey.grey100};
  color: ${palette.grey.grey700};
  font-size: 0.75rem;
  font-weight: 500;
`

const formatCount = (value: number) => value.toLocaleString('en-US')

interface LocationCardProps {
  card: DashboardLocationCard
  onClick: (card: DashboardLocationCard) => void
}

const LocationCard = ({ card, onClick }: LocationCardProps) => {
  const [sitesExpanded, setSitesExpanded] = useState(false)
  const sites = [...card.worksites].sort(
    (a, b) => b.employeeCount - a.employeeCount
  )
  const hiddenSites = Math.max(sites.length - VISIBLE_SITES, 0)
  const visibleSites = sitesExpanded ? sites : sites.slice(0, VISIBLE_SITES)
  const toggleSites = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setSitesExpanded((open) => !open)
  }

  return (
    <Card
      type='button'
      data-cy='dashboard-location-card'
      onClick={() => onClick(card)}
      aria-label={`View ${card.city} location details`}
    >
      <Header>
        <Text variant='action2' fontWeight={600} noWrap>
          {card.city}
        </Text>
        <ActiveBadge>Active</ActiveBadge>
      </Header>

      <Metrics>
        <Metric>
          <Text variant='h2' fontWeight={600}>
            {formatCount(card.employeeCount)}
          </Text>
          <Text variant='body1' color={palette.text.secondary}>
            Employees
          </Text>
        </Metric>
        <Metric>
          <Text variant='h2' fontWeight={600}>
            {card.enrollmentRate}%
          </Text>
          <Text variant='body1' color={palette.text.secondary}>
            Enrolled
          </Text>
        </Metric>
      </Metrics>

      {visibleSites.length > 0 ? (
        <Sites>
          {visibleSites.map((site) => (
            <SiteChip key={site.id}>
              {site.alias}
              <SiteCount>{formatCount(site.employeeCount)}</SiteCount>
            </SiteChip>
          ))}
          {hiddenSites > 0 ? (
            <MoreSites
              role='button'
              tabIndex={0}
              onClick={toggleSites}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  toggleSites(event)
                }
              }}
            >
              {sitesExpanded
                ? 'Show less'
                : `+${hiddenSites} more ${hiddenSites === 1 ? 'site' : 'sites'}`}
            </MoreSites>
          ) : null}
        </Sites>
      ) : null}

      {card.programCategories.length > 0 ? (
        <Tags>
          {card.programCategories.map((category) => (
            <Tag key={category}>{category}</Tag>
          ))}
        </Tags>
      ) : (
        <Tags>
          <Text variant='caption' color={palette.text.secondary}>
            No active programs
          </Text>
        </Tags>
      )}
    </Card>
  )
}

export default React.memo(LocationCard)
